import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Doctor, Staff, Room, ScheduleItem, Appointment, Task } from './SchedulerTypes';
import DailyViewActions from './DailyViewActions';
import DailyViewTabs from './DailyViewTabs';
import DailyViewModals from './DailyViewModals';
import { Reassignment } from './ConflictResolutionModal';
import ManpowerScheduleModal from './ManpowerScheduleModal';
import BackupDAScheduleModal from './BackupDAScheduleModal';
import { useDailyViewState } from '../hooks/useDailyViewState';
import { generateDateRange, createScheduleItemsForDateRange, formatDateToString, expandMultiDaySchedules } from '../lib/dateUtils';
import { captureAndDownloadAsA4Image, formatDateForFilename } from '../lib/imageUtils';
import { useClinic } from '@/contexts/ClinicContext';

interface DailyViewProps {
  currentDate: Date;
  scheduleItems: ScheduleItem[];
  appointments: Appointment[];
  tasks: Task[];
  doctors: Doctor[];
  staff: Staff[];
  rooms: Room[];
  initialTab?: string;
  onAddScheduleItem: (item: Omit<ScheduleItem, 'id' | 'created_at' | 'updated_at'>, reassignments?: Reassignment[]) => void;
  onUpdateScheduleItem: (id: string, updates: Partial<ScheduleItem>) => void;
  onDeleteScheduleItem: (id: string) => void;
  onDeleteScheduleSeries: (ids: string[]) => void;
  onAddAppointment: (appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateAppointment: (id: string, updates: Partial<Appointment>) => void;
  onDeleteAppointment: (id: string) => void;
  onAddTask: (task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onDeleteTask: (id: string) => void;
  getAppointmentForScheduleItem: (scheduleItemId: string) => Appointment | undefined;
  getTaskForScheduleItem: (scheduleItemId: string) => Task | undefined;
  onCopyScheduleItems: (date: string) => void;
  onPasteScheduleItems: (date: string) => void;
  copiedItemsCount: number;
  isLoadingSchedules?: boolean;
  isFetchingSchedules?: boolean;
  onRefreshSchedule?: () => void;
}

const DailyView = ({
  currentDate,
  scheduleItems,
  appointments,
  tasks,
  doctors,
  staff,
  rooms,
  initialTab,
  onAddScheduleItem,
  onUpdateScheduleItem,
  onDeleteScheduleItem,
  onDeleteScheduleSeries,
  onAddAppointment,
  onUpdateAppointment,
  onDeleteAppointment,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  getAppointmentForScheduleItem,
  getTaskForScheduleItem,
  onCopyScheduleItems,
  onPasteScheduleItems,
  copiedItemsCount,
  isLoadingSchedules = false,
  isFetchingSchedules = false,
  onRefreshSchedule,
}: DailyViewProps) => {
  const { currentClinic } = useClinic();
  const [isManpowerModalOpen, setIsManpowerModalOpen] = useState(false);
  const [isBackupDAModalOpen, setIsBackupDAModalOpen] = useState(false);
  const dailyViewRef = useRef<HTMLDivElement>(null);

  const {
    isScheduleModalOpen,
    selectedTimeSlot,
    selectedPerson,
    selectedIsDoctor,
    editingItem,
    openScheduleModal,
    closeScheduleModal,
    isAppointmentModalOpen,
    selectedScheduleForAppointment,
    editingAppointment,
    openAppointmentModal,
    closeAppointmentModal,
    isTaskModalOpen,
    selectedScheduleForTask,
    editingTask,
    openTaskModal,
    closeTaskModal,
  } = useDailyViewState();

  const dateStr = formatDateToString(currentDate);
  
  // Expand multi-day schedules to show on all applicable days
  const expandedScheduleItems = useMemo(() => {
    return expandMultiDaySchedules(scheduleItems, dateStr, dateStr);
  }, [scheduleItems, dateStr]);
  
  // Debug logging
  console.log('[DailyView] Component props:', {
    roomsCount: rooms?.length || 0,
    roomsFirstItem: rooms?.[0],
    appointmentsCount: appointments?.length || 0,
    hasGetAppointmentFunc: !!getAppointmentForScheduleItem,
    originalScheduleItems: scheduleItems?.length || 0,
    expandedScheduleItems: expandedScheduleItems?.length || 0
  });

  // Generate time slots from 9:00 AM to 6:00 PM in 15-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    // Daily view runs 09:00 through 20:30 (8:30pm) in 15-minute steps.
    for (let hour = 9; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === 20 && minute > 30) break; // stop at 20:30
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(time);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handleCellClick = (time: string, personId: string, isDoctor: boolean = false) => {
    openScheduleModal(time, personId, isDoctor);
  };

  const handleEditItem = (item: ScheduleItem) => {
    // Extract base UUID from composite IDs (removes _day_X suffix)
    const baseId = item.id.split('_day_')[0];
    
    // Find the original schedule item using the base ID
    const originalItem = scheduleItems.find(si => si.id === baseId) || item;
    
    openScheduleModal(undefined, undefined, undefined, originalItem);
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    console.log('[DailyView] Deleting schedule item:', scheduleId);
    // Extract base UUID from composite IDs (removes _day_X suffix)
    const baseId = scheduleId.split('_day_')[0];
    onDeleteScheduleItem(baseId);
    closeScheduleModal();
  };

  const handleDeleteSeries = (ids: string[]) => {
    onDeleteScheduleSeries(ids);
    closeScheduleModal();
  };

  const handleBookAppointment = (schedule: ScheduleItem) => {
    openAppointmentModal(schedule);
  };

  const handleViewAppointment = (appointment: Appointment) => {
    const schedule = expandedScheduleItems.find(item => 
      item.originalId === appointment.scheduleItemId || item.id === appointment.scheduleItemId
    );
    if (schedule) {
      openAppointmentModal(schedule, appointment);
    }
  };

  const handleAssignTask = (schedule: ScheduleItem) => {
    openTaskModal(schedule);
  };

  const handleViewTask = (task: Task) => {
    const schedule = expandedScheduleItems.find(item => 
      item.originalId === task.scheduleItemId || item.id === task.scheduleItemId
    );
    if (schedule) {
      openTaskModal(schedule, task);
    }
  };

  // Memoized helper function for processing schedule data
  const processScheduleData = useCallback((scheduleData: Partial<ScheduleItem>, isEditing: boolean): Partial<ScheduleItem>[] => {
    if (isEditing) {
      return [scheduleData];
    }

    // Create new schedule item(s)
    const startDate = new Date(scheduleData.date!);
    const endDate = scheduleData.endDate ? new Date(scheduleData.endDate) : undefined;
    
    // Store original times before any modifications
    const originalStartTime = selectedTimeSlot || scheduleData.startTime || '09:00';
    const originalEndTime = scheduleData.endTime || '10:00';
    
    // Generate date range for recurring schedules
    const dateRange = generateDateRange(startDate, endDate);
    
    if (dateRange.length === 1) {
      // Single day schedule - use the form's data exactly as provided
      const newScheduleData = {
        ...scheduleData,
        startTime: originalStartTime,
        endTime: originalEndTime,
      };

      // Only override person fields if they're not already set in scheduleData
      // This preserves the user's selection from the form
      if (!newScheduleData.doctorId && !newScheduleData.staffId && selectedPerson) {
        // Only set person fields if none were provided in the form
        if (selectedIsDoctor) {
          newScheduleData.doctorId = selectedPerson;
        } else {
          newScheduleData.staffId = selectedPerson;
        }
      }

      return [newScheduleData];
    } else {
      // Multi-day recurring schedule
      const baseScheduleData = {
        ...scheduleData,
        startTime: originalStartTime,
        endTime: originalEndTime,
        type: scheduleData.type || 'appointment',
        title: scheduleData.title || '',
      };

      // Only override person fields if they're not already set in scheduleData
      if (!baseScheduleData.doctorId && !baseScheduleData.staffId && selectedPerson) {
        // Only set person fields if none were provided in the form
        if (selectedIsDoctor) {
          baseScheduleData.doctorId = selectedPerson;
        } else {
          baseScheduleData.staffId = selectedPerson;
        }
      }

      // Create schedule items for each date in the range with appropriate times
      return dateRange.map(date => ({
        ...baseScheduleData,
        date,
        startTime: originalStartTime,
        endTime: originalEndTime,
      }));
    }
  }, [selectedTimeSlot, selectedIsDoctor, selectedPerson]);

  const handleSaveSchedule = useCallback((scheduleData: Partial<ScheduleItem>, reassignments?: Reassignment[]) => {
    console.log('[DailyView] handleSaveSchedule called with data and reassignments:', { scheduleData, reassignments });
    
    if (editingItem) {
      // Update existing schedule item
      const baseId = editingItem.id.split('_day_')[0];
      console.log('[DailyView] Updating existing item:', editingItem.id, 'baseId:', baseId);
      onUpdateScheduleItem(baseId, scheduleData);
    } else {
      // Process and create new schedule items
      const scheduleItemsToCreate = processScheduleData(scheduleData, false);
      console.log('[DailyView] Processing schedule data resulted in items:', scheduleItemsToCreate);
      
      // Add each schedule item with potential reassignments (only apply to first item)
      scheduleItemsToCreate.forEach((item, index) => {
        console.log(`[DailyView] Creating schedule item ${index + 1}:`, item);
        // Pass reassignments only with the first item (the leave/MC schedule)
        onAddScheduleItem(item as any, index === 0 ? reassignments : undefined);
      });
    }
    closeScheduleModal();
  }, [editingItem, onUpdateScheduleItem, onAddScheduleItem, processScheduleData, closeScheduleModal]);

  const handleSaveAppointment = (appointmentData: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingAppointment) {
      onUpdateAppointment(editingAppointment.id, appointmentData);
    } else {
      onAddAppointment(appointmentData);
    }
    closeAppointmentModal();
  };

  const handleSaveTask = (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingTask) {
      onUpdateTask(editingTask.id, taskData);
    } else {
      onAddTask(taskData);
    }
    closeTaskModal();
  };

  const handleExtractAsImage = async () => {
    if (!dailyViewRef.current) {
      console.error('Daily view element not found');
      return;
    }

    try {
      // Find the active table panel within the daily view
      const activeTabPanel = dailyViewRef.current.querySelector('[role="tabpanel"]:not([hidden])') ||
                             dailyViewRef.current.querySelector('[data-state="active"]') ||
                             dailyViewRef.current.querySelector('.space-y-6 > div:nth-child(2)') ||
                             dailyViewRef.current;
      
      console.log('Capturing element:', activeTabPanel);
      
      const dateStr = formatDateForFilename(currentDate);
      const filename = `daily-schedule-${dateStr}`;
      await captureAndDownloadAsA4Image(activeTabPanel as HTMLElement, filename);
    } catch (error) {
      console.error('Failed to extract image:', error);
      // You could add a toast notification here
    }
  };

  return (
    <div ref={dailyViewRef} className="space-y-4 sm:space-y-6">
      <DailyViewActions
        onAddSchedule={() => openScheduleModal()}
        onCopyDay={() => onCopyScheduleItems(dateStr)}
        onPasteDay={() => onPasteScheduleItems(dateStr)}
        onShowManpowerSchedule={() => setIsManpowerModalOpen(true)}
        onShowBackupDASchedule={() => setIsBackupDAModalOpen(true)}
        onExtractAsImage={handleExtractAsImage}
        copiedItemsCount={copiedItemsCount}
        currentDate={currentDate}
        onRefreshSchedule={onRefreshSchedule}
      />

      <DailyViewTabs
        timeSlots={timeSlots}
        currentDate={currentDate}
        scheduleItems={expandedScheduleItems}
        appointments={appointments}
        tasks={tasks}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        initialTab={initialTab}
        getAppointmentForScheduleItem={getAppointmentForScheduleItem}
        getTaskForScheduleItem={getTaskForScheduleItem}
        onCellClick={handleCellClick}
        onEditItem={handleEditItem}
        onBookAppointment={handleBookAppointment}
        onViewAppointment={handleViewAppointment}
        onAssignTask={handleAssignTask}
        onViewTask={handleViewTask}
        onAddScheduleItem={onAddScheduleItem}
        onDeleteScheduleItem={onDeleteScheduleItem}
        isLoadingSchedules={isLoadingSchedules}
        isFetchingSchedules={isFetchingSchedules}
      />

      <DailyViewModals
        isScheduleModalOpen={isScheduleModalOpen}
        onCloseScheduleModal={closeScheduleModal}
        onSaveSchedule={handleSaveSchedule}
        onDeleteSchedule={handleDeleteSchedule}
        onDeleteSeries={handleDeleteSeries}
        onCreateAppointment={onAddAppointment}
        editingItem={editingItem}
        defaultStartTime={selectedTimeSlot}
        currentDate={currentDate}
        selectedPersonId={selectedPerson}
        selectedIsDoctor={selectedIsDoctor}
        isAppointmentModalOpen={isAppointmentModalOpen}
        onCloseAppointmentModal={closeAppointmentModal}
        onSaveAppointment={handleSaveAppointment}
        selectedScheduleForAppointment={selectedScheduleForAppointment}
        editingAppointment={editingAppointment}
        isTaskModalOpen={isTaskModalOpen}
        onCloseTaskModal={closeTaskModal}
        onSaveTask={handleSaveTask}
        selectedScheduleForTask={selectedScheduleForTask}
        editingTask={editingTask}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        scheduleItems={expandedScheduleItems}
      />

      <ManpowerScheduleModal
        isOpen={isManpowerModalOpen}
        onClose={() => setIsManpowerModalOpen(false)}
        currentDate={currentDate}
        scheduleItems={expandedScheduleItems}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        currentClinicName={currentClinic?.name === 'Forum The Shopping Mall' ? 'Forum' : (currentClinic?.name || 'Forum')}
      />

      <BackupDAScheduleModal
        isOpen={isBackupDAModalOpen}
        onClose={() => setIsBackupDAModalOpen(false)}
        currentDate={currentDate}
        doctors={doctors}
        staff={staff}
        rooms={rooms}
        scheduleItems={expandedScheduleItems}
      />
    </div>
  );
};

export default DailyView;
